import {
  addCalendarDays,
  formatCalendarDate,
  formatHourMinutes,
  HOUR_TRACK,
} from '@repo/design-system';
import type { HourTrackRange, MiniWeekDay } from '@repo/design-system';
import type {
  AuthoredOpeningHoursDay,
  AuthoredOpeningHoursVersion,
  AuthoredSpecialWrite,
  AuthoredStandardWrite,
  OpeningHoursRead,
} from '@repo/frontend-types';

/**
 * The Opening hours step's model (spec twin-details-page-hours-week, R5): the conversions between
 * the store's authored days and the design-system hour track, the calendars grouped by the twin's
 * own date, every sentence the step derives from them, and the bulk changes. Pure — no React, no
 * I/O — so every rule is tested on real inputs.
 *
 * Dates are `YYYY-MM-DD` calendar dates, handled only as UTC midnights, so the browser's own zone
 * never moves one; "today" is always the twin's (`launchHours.today`).
 */

export type Weekday = AuthoredOpeningHoursDay['dayOfWeek'];
export type DayState = AuthoredOpeningHoursDay['state'];

export const WEEKDAYS: readonly Weekday[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const LONG: Record<Weekday, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

/** `Monday`. */
export const dayName = (day: Weekday): string => LONG[day];
/** `Mon`, as frame 06 labels a row. */
export const dayShort = (day: Weekday): string => LONG[day].slice(0, 3);
/** The weekday after `day`; Sunday wraps to Monday. */
export const nextWeekday = (day: Weekday): Weekday => WEEKDAYS[(WEEKDAYS.indexOf(day) + 1) % 7];

// ── Times ───────────────────────────────────────────────────────────────────────────────────

/** `HH:mm` (or `HH:mm:ss`) → minutes after midnight. */
export const toMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(':');
  return Number(h) * 60 + Number(m);
};

/**
 * One authored day → the hour track's ranges, in opening order. A range closing the next day ends at
 * `close + 1440`, so Friday 06:00 – 01:00 is `{ 360, 1500 }` and a midnight close is `end: 1440`.
 * Closed and 24-hour days carry none (the track draws 24 hours from its own `allDay`).
 */
export const toTrackRanges = (day: AuthoredOpeningHoursDay): HourTrackRange[] =>
  day.state !== 'ranges'
    ? []
    : day.ranges
        .map(r => ({
          start: toMinutes(r.open),
          end: toMinutes(r.close) + (r.closesNextDay ? HOUR_TRACK.midnight : 0),
        }))
        .sort((a, b) => a.start - b.start);

/**
 * The hour track's ranges → one authored day, in opening order (the server answers a save with the
 * ranges in the order they were sent). An end from 1440 on closes the next day; exactly 1440 is a
 * midnight close, `00:00` the next day, since the store does not yet accept `24:00`.
 */
export const toAuthoredDay = (
  dayOfWeek: Weekday,
  state: DayState,
  ranges: readonly HourTrackRange[],
): AuthoredOpeningHoursDay => ({
  dayOfWeek,
  state,
  ranges:
    state !== 'ranges'
      ? []
      : [...ranges]
          .sort((a, b) => a.start - b.start)
          .map(r => ({
            open: formatHourMinutes(r.start),
            close: formatHourMinutes(r.end),
            closesNextDay: r.end >= HOUR_TRACK.midnight,
          })),
});

/** One day's state and ranges, range order aside — the rule `sameDays` applies to each day. */
const dayKey = (d: AuthoredOpeningHoursDay | undefined) =>
  d
    ? `${d.state}:${[...d.ranges]
        .map(r => `${r.open.slice(0, 5)}-${r.close.slice(0, 5)}${r.closesNextDay ? '+' : ''}`)
        .sort()
        .join(',')}`
    : '?';

const dayOf = (days: readonly AuthoredOpeningHoursDay[], w: Weekday) =>
  days.find(d => d.dayOfWeek === w);

/** Whether two weeks hold the same days, range order aside. */
export const sameDays = (
  a: readonly AuthoredOpeningHoursDay[],
  b: readonly AuthoredOpeningHoursDay[],
): boolean => WEEKDAYS.every(w => dayKey(dayOf(a, w)) === dayKey(dayOf(b, w)));

// ── Dates ───────────────────────────────────────────────────────────────────────────────────

/** `date` moved by `days` — the design system's, which the date field steps with too. */
export const addDays = (date: string, days: number): string => addCalendarDays(date, days);

/** The latest an open-ended standard may start: the store publishes an open end as 2035-04-15. */
export const OPEN_END_LATEST_START = '2035-04-15';

// ── Calendars by time ───────────────────────────────────────────────────────────────────────

const standardsOf = (versions: readonly AuthoredOpeningHoursVersion[]) =>
  versions.filter(v => v.kind === 'standard');

/** Whether `v` governs date `d`; an open end runs on. */
const covers = (v: AuthoredOpeningHoursVersion, d: string) =>
  v.effectiveDateFrom <= d && (v.effectiveDateTo === null || v.effectiveDateTo >= d);

export type CalendarGroup = 'now' | 'upcoming' | 'past';

export interface Calendars {
  /** The standard covering today, or null. */
  now: AuthoredOpeningHoursVersion | null;
  /** Standards starting after today, earliest first. */
  upcoming: AuthoredOpeningHoursVersion[];
  /** Standards ended before today, latest first. */
  past: AuthoredOpeningHoursVersion[];
}

/**
 * The standard calendars by the twin's date (spec R5.2). Standards only: the launch rule's readers
 * (`startPanelShows`, `copySourceWeek`) need standards, and a special period never makes a twin
 * launchable. Special periods are grouped by `groupSpecials`, and the list merges the two.
 */
export const groupCalendars = (
  versions: readonly AuthoredOpeningHoursVersion[],
  today: string,
): Calendars => {
  const of = (group: CalendarGroup) =>
    standardsOf(versions).filter(v => groupOf(v, today) === group);
  return {
    now: of('now')[0] ?? null,
    upcoming: of('upcoming').sort((a, b) => a.effectiveDateFrom.localeCompare(b.effectiveDateFrom)),
    past: of('past').sort((a, b) =>
      (b.effectiveDateTo as string).localeCompare(a.effectiveDateTo as string),
    ),
  };
};

/**
 * Which group a calendar is in today — the one rule the list, the panel's read-only state and
 * *Remove* all read.
 */
export const groupOf = (v: AuthoredOpeningHoursVersion, today: string): CalendarGroup =>
  v.effectiveDateFrom > today
    ? 'upcoming'
    : // A special stored without an end is one day long (the Inputs table), never open-ended.
      covers(
          v.kind === 'special'
            ? { ...v, effectiveDateTo: v.effectiveDateTo ?? v.effectiveDateFrom }
            : v,
          today,
        )
      ? 'now'
      : 'past';

/**
 * Whether the step opens on the start panel (spec twin-details-page-hours-start, R1.1): no standard
 * covers today or starts later. The step and the page's organisation-twins read both ask this.
 */
export const startPanelShows = (
  versions: readonly AuthoredOpeningHoursVersion[],
  today: string,
): boolean => {
  const { now, upcoming } = groupCalendars(versions, today);
  return now === null && upcoming.length === 0;
};

/** A calendar's identity across saves: kind and start date, as the store keys it. */
export const calendarKey = (v: Pick<AuthoredOpeningHoursVersion, 'kind' | 'effectiveDateFrom'>) =>
  `${v.kind}:${v.effectiveDateFrom}`;

// ── Wording ─────────────────────────────────────────────────────────────────────────────────

const date = formatCalendarDate;

/** The list card's second line: *From 1 Nov 2026 · no end*, *From 14 Feb 2026 · until 3 Mar 2035*, or a past span. */
export const calendarListSub = (v: AuthoredOpeningHoursVersion, group: CalendarGroup): string =>
  group === 'past'
    ? `${date(v.effectiveDateFrom)} – ${date(v.effectiveDateTo as string)}`
    : v.effectiveDateTo === null
      ? `From ${date(v.effectiveDateFrom)} · no end`
      : `From ${date(v.effectiveDateFrom)} · until ${date(v.effectiveDateTo)}`;

/**
 * The folded Past card: *2 earlier calendars* over *Ended 31 Oct 2026*. Standards and special
 * periods both count (spec twin-details-page-hours-special, R1.8); `past` is latest end first.
 */
export const pastSummary = (
  past: readonly AuthoredOpeningHoursVersion[],
): { title: string; sub: string } | null =>
  past.length === 0
    ? null
    : {
        title: `${past.length} earlier calendar${past.length === 1 ? '' : 's'}`,
        sub: `Ended ${date(past[0].effectiveDateTo ?? past[0].effectiveDateFrom)}`,
      };

/**
 * The week panel's sub line (frame 06): *From 1 Nov 2026 · no end date · Europe/London*. The zone is
 * left out when the read did not carry one (an older middleware, R12) — never printed as nothing.
 */
export const panelSub = (
  v: AuthoredOpeningHoursVersion,
  group: CalendarGroup,
  timezone?: string | null,
): string => {
  const span =
    group === 'past'
      ? `${date(v.effectiveDateFrom)} – ${date(v.effectiveDateTo as string)} · ended`
      : v.effectiveDateTo === null
        ? `From ${date(v.effectiveDateFrom)} · no end date`
        : `From ${date(v.effectiveDateFrom)} · until ${date(v.effectiveDateTo)}`;
  return timezone ? `${span} · ${timezone}` : span;
};

/**
 * The line under a day's times about midnight (frame 06): *Closes Saturday 01:00* for a range past
 * midnight, *Closes at midnight* for one ending exactly then, else null. Only the last range of a
 * day can run past midnight.
 */
export const midnightLine = (day: Weekday, ranges: readonly HourTrackRange[]): string | null => {
  const last = [...ranges].sort((a, b) => a.start - b.start).pop();
  if (!last || last.end < HOUR_TRACK.midnight) return null;
  if (last.end === HOUR_TRACK.midnight) return 'Closes at midnight';
  return `Closes ${dayName(nextWeekday(day))} ${formatHourMinutes(last.end)}`;
};

/**
 * Where *Add range* puts a second range (spec R7.5): two hours, starting an hour after the first
 * closes, if that ends by midnight; else ending an hour before the first opens; else nowhere.
 */
export const secondRangeFor = (first: HourTrackRange): HourTrackRange | null => {
  const after = { start: first.end + 60, end: first.end + 180 };
  if (after.end <= HOUR_TRACK.midnight) return after;
  const before = { start: first.start - 180, end: first.start - 60 };
  if (before.start >= 0) return before;
  return null;
};

// ── Bulk changes ────────────────────────────────────────────────────────────────────────────

const cloneDay = (d: AuthoredOpeningHoursDay, as: Weekday): AuthoredOpeningHoursDay => ({
  dayOfWeek: as,
  state: d.state,
  ranges: d.ranges.map(r => ({ ...r })),
});

/** A deep copy of a week, so a send never shares arrays with a constant or another twin's read. */
export const cloneWeek = (days: readonly AuthoredOpeningHoursDay[]): AuthoredOpeningHoursDay[] =>
  days.map(d => cloneDay(d, d.dayOfWeek));

/** *Copy Monday to Weekdays*: Tuesday to Friday take Monday's state and ranges. */
export const copyMondayToWeekdays = (
  days: readonly AuthoredOpeningHoursDay[],
): AuthoredOpeningHoursDay[] => {
  const monday = days.find(d => d.dayOfWeek === 'monday');
  if (!monday) return days.map(d => cloneDay(d, d.dayOfWeek));
  return days.map(d =>
    ['tuesday', 'wednesday', 'thursday', 'friday'].includes(d.dayOfWeek)
      ? cloneDay(monday, d.dayOfWeek)
      : cloneDay(d, d.dayOfWeek),
  );
};

/** *Set 24/7*: every day open 24 hours. */
export const setEveryDay24h = (
  days: readonly AuthoredOpeningHoursDay[],
): AuthoredOpeningHoursDay[] =>
  WEEKDAYS.map(w => ({ dayOfWeek: w, state: 'open_24h' as const, ranges: [] }));

/** Every day Closed. */
export const closedWeek = (): AuthoredOpeningHoursDay[] =>
  WEEKDAYS.map(w => ({ dayOfWeek: w, state: 'closed' as const, ranges: [] }));

// ── Quick starts (spec twin-details-page-hours-start, R3) ───────────────────────────────────

const openEachDay = (hours: (day: Weekday) => HourTrackRange): AuthoredOpeningHoursDay[] =>
  WEEKDAYS.map(w => toAuthoredDay(w, 'ranges', [hours(w)]));

const WEEKEND: readonly Weekday[] = ['saturday', 'sunday'];

/*
 * The quick starts are functions, not constants: each call builds a fresh week, so no caller can
 * share or mutate one, and nothing here runs `@repo/design-system` code while the module loads — a
 * suite that mocks that package without `formatHourMinutes` still imports this file.
 */

/** *Same every day*: 06:00–22:00 on all seven days (Maria, 2026-10-19). */
export const sameEveryDay = (): AuthoredOpeningHoursDay[] =>
  openEachDay(() => ({ start: 6 * 60, end: 22 * 60 }));

/** *Weekdays and weekends*: Monday–Friday 06:00–22:00, Saturday and Sunday 08:00–20:00 (Maria, 2026-10-19). */
export const weekdaysAndWeekends = (): AuthoredOpeningHoursDay[] =>
  openEachDay(w =>
    WEEKEND.includes(w) ? { start: 8 * 60, end: 20 * 60 } : { start: 6 * 60, end: 22 * 60 },
  );

/** *Open 24/7*: every day open 24 hours. */
export const open247 = (): AuthoredOpeningHoursDay[] => setEveryDay24h([]);

/**
 * The body for a quick start (R5), less the etag: a standard week from the twin's `today`, with no
 * end. Only sent when nothing covers today or later, so it ends nothing and replaces nothing.
 */
export const startWeekBody = (
  today: string,
  days: readonly AuthoredOpeningHoursDay[],
): Omit<AuthoredStandardWrite, 'expectedEtag'> => ({
  kind: 'standard',
  name: null,
  effectiveDateFrom: today,
  effectiveDateTo: null,
  days: cloneWeek(days),
});

/**
 * The week *Copy another twin* takes from a source twin's read (R4.4.2): the standard covering the
 * source's own today, else its earliest upcoming standard, else null. A read with no `today` (the
 * source has no timezone, so its dates cannot be placed) gives the standard with the latest start
 * instead — the most recently authored week — else null.
 */
export const copySourceWeek = (read: OpeningHoursRead): AuthoredOpeningHoursVersion | null => {
  const versions = read.authored.versions;
  const today = read.launchHours.today;
  if (today === null) {
    return (
      [...standardsOf(versions)].sort((a, b) =>
        b.effectiveDateFrom.localeCompare(a.effectiveDateFrom),
      )[0] ?? null
    );
  }
  const { now, upcoming } = groupCalendars(versions, today);
  return now ?? upcoming[0] ?? null;
};

/**
 * A week as the design system's mini week draws it (R7.3), Monday first: Closed is an empty cell,
 * 24 hours a full one, and a range past midnight is drawn to the bottom of its own day and its
 * remainder at the top of the next day's cell — Sunday's into Monday's — since the hours after
 * midnight count to the day they open on, and are spent on the next morning's clock.
 */
export const miniWeekOf = (days: readonly AuthoredOpeningHoursDay[]): MiniWeekDay[] => {
  const cells: { allDay: boolean; ranges: HourTrackRange[] }[] = WEEKDAYS.map(() => ({
    allDay: false,
    ranges: [],
  }));
  WEEKDAYS.forEach((w, i) => {
    const day = days.find(d => d.dayOfWeek === w);
    if (!day) return;
    if (day.state === 'open_24h') {
      cells[i].allDay = true;
      return;
    }
    toTrackRanges(day).forEach(r => {
      cells[i].ranges.push({ start: r.start, end: Math.min(r.end, HOUR_TRACK.midnight) });
      if (r.end > HOUR_TRACK.midnight) {
        cells[(i + 1) % 7].ranges.push({ start: 0, end: r.end - HOUR_TRACK.midnight });
      }
    });
  });
  return cells.map(c =>
    c.allDay
      ? { allDay: true, ranges: [] }
      : { ranges: [...c.ranges].sort((a, b) => a.start - b.start) },
  );
};

// ── New standard hours from a date ──────────────────────────────────────────────────────────

/**
 * The body for *New Standard Hours From a Date* on `from` (spec R5.5), less the etag. Its week is a
 * copy of the standard in force the day before (else every day Closed); it ends the standard
 * covering `from` on the day before only when there is one — the store refuses
 * `endsPreviousStandard` with nothing to end. It ends on `to`, or has no end (spec
 * opening-hours-edit-standard-details, R4.2, which replaced the take-over of the ended week's end),
 * and carries the name as stored (`standardNameStored`).
 */
export const newStandardFromDate = (
  versions: readonly AuthoredOpeningHoursVersion[],
  { from, to = null, name = null }: { from: string; to?: string | null; name?: string | null },
): {
  body: Omit<AuthoredStandardWrite, 'expectedEtag'>;
  /** The standard whose week was copied, for the popover's sentence; null when the week is Closed. */
  copiedFrom: AuthoredOpeningHoursVersion | null;
  /** The standard this one ends on the day before `from`, or null. */
  ends: AuthoredOpeningHoursVersion | null;
} => {
  const standards = standardsOf(versions);
  const covering = standards.find(v => covers(v, from)) ?? null;
  const source = standards.find(v => covers(v, addDays(from, -1))) ?? null;
  return {
    body: {
      kind: 'standard',
      name: standardNameStored(name),
      effectiveDateFrom: from,
      effectiveDateTo: to,
      days: source ? source.days.map(d => cloneDay(d, d.dayOfWeek)) : closedWeek(),
      ...(covering ? { endsPreviousStandard: true } : {}),
    },
    copiedFrom: source,
    ends: covering,
  };
};

/**
 * The dates *New Standard Hours From a Date* may start on (spec R5.6), both ends included:
 *
 * - `min`, the later of today and the day after the latest standard's start — the store refuses a
 *   start on or before any existing standard's start;
 * - `max`, 2035-04-15 (an open-ended standard must start by then), pulled to the day after the
 *   latest standard's end only when that end is today or later — a later date would leave a gap in
 *   the future. A latest standard that has already ended does not limit it: a gap in the past is
 *   history, not a hole.
 */
export const newStandardDateLimits = (
  versions: readonly AuthoredOpeningHoursVersion[],
  today: string,
): { min: string; max: string; available: boolean } => {
  const standards = standardsOf(versions);
  const latest = [...standards].sort((a, b) =>
    b.effectiveDateFrom.localeCompare(a.effectiveDateFrom),
  )[0];
  let min = today;
  let max = OPEN_END_LATEST_START;
  if (latest) {
    const dayAfterStart = addDays(latest.effectiveDateFrom, 1);
    if (dayAfterStart > min) min = dayAfterStart;
    if (latest.effectiveDateTo !== null && latest.effectiveDateTo >= today) {
      const dayAfterEnd = addDays(latest.effectiveDateTo, 1);
      if (dayAfterEnd < max) max = dayAfterEnd;
    }
  }
  return { min, max, available: min <= max };
};

// ── A standard's name and dates (spec opening-hours-edit-standard-details) ─────────────────

/** What the step shows for a standard with no name (R5.1); sent as a name, it is stored as none. */
export const STANDARD_DEFAULT_NAME = 'Standard hours';
/** The store's longest standard name, trimmed (R1.2). */
export const STANDARD_NAME_MAX = 80;
export const STANDARD_NAME_TOO_LONG = `A name can be at most ${STANDARD_NAME_MAX} characters`;
/** The store's latest date, and so the latest a standard may end. */
export const STANDARD_MAX_DATE = '2099-12-31';

/** A standard's name as the step shows it: its own, else *Standard hours* (R5.1). */
export const standardName = (v: Pick<AuthoredOpeningHoursVersion, 'name'>): string =>
  v.name?.trim() || STANDARD_DEFAULT_NAME;

/**
 * A typed name as the store keeps it (the store's R1.2, mirrored): trimmed, and none for a blank one
 * or exactly *Standard hours*.
 */
export const standardNameStored = (text: string | null | undefined): string | null => {
  const trimmed = (text ?? '').trim();
  return trimmed.length === 0 || trimmed === STANDARD_DEFAULT_NAME ? null : trimmed;
};

/** Why a typed standard name cannot be saved (R3.9), else null. */
export const standardNameProblem = (raw: string): string | null =>
  raw.trim().length > STANDARD_NAME_MAX ? STANDARD_NAME_TOO_LONG : null;

/** A standard's name and dates as the Edit details popover holds them. */
export interface StandardFields {
  name: string;
  from: string;
  /** Null: no end date. */
  to: string | null;
}

/** The New and Edit popovers' fields as typed: a date may be missing, and the switch is its own. */
export interface StandardDraft {
  name: string;
  from: string | null;
  to: string | null;
  noEnd: boolean;
}

/** A draft as the fields it would send, or null while a date it needs is missing. */
export const draftFields = (d: StandardDraft): StandardFields | null =>
  d.from && (d.noEnd || d.to) ? { name: d.name, from: d.from, to: d.noEnd ? null : d.to } : null;

/** A set next to the one edited that the same save moves (R2.2), with its new dates. */
export interface StandardMove {
  version: AuthoredOpeningHoursVersion;
  from: string;
  to: string | null;
}

export interface StandardEditLimits {
  /** A future set's start may move; a running set's stays (R3.2). */
  startEditable: boolean;
  /** The start's range (future sets only). Its latest, with an end chosen, is that end. */
  start: { min: string; max: string };
  /** The end's range; its earliest is also never before the start. */
  end: { min: string; max: string };
  /** Whether *No end date* may be on, and when not, why — the switch's description (R3.3). */
  noEnd: { offered: boolean; reason: string | null };
  /** The standard ending the day before this one's stored start, which a moved start moves. */
  previous: AuthoredOpeningHoursVersion | null;
  /** The standard starting the day after this one's stored end, which a moved end moves. */
  next: AuthoredOpeningHoursVersion | null;
}

/**
 * The dates the Edit details popover allows for `calendar` (R3.3), from the sets as stored and the
 * twin's `today`. `from` is the start as chosen, for the no-end rule (an open end must start by
 * 15 Apr 2035).
 *
 * - **Start** (future sets): from the later of today and the day after the adjoining previous set's
 *   start (it moves, keeping a day); across a gap, the day after that set's end. Up to the chosen
 *   end, or 15 Apr 2035 with no end.
 * - **End**: from the later of today and the start. Up to the day before the adjoining next set's
 *   last day (it moves, keeping a day), or 14 Apr 2035 when that set has no end; across a gap, the
 *   day before that set's start; else 31 Dec 2099.
 */
export const standardEditLimits = (
  versions: readonly AuthoredOpeningHoursVersion[],
  calendar: AuthoredOpeningHoursVersion,
  today: string,
  from: string = calendar.effectiveDateFrom,
): StandardEditLimits => {
  const others = standardsOf(versions).filter(v => calendarKey(v) !== calendarKey(calendar));
  const dayBefore = addDays(calendar.effectiveDateFrom, -1);
  const previous = others.find(v => v.effectiveDateTo === dayBefore) ?? null;
  const before = others
    .filter(v => v.effectiveDateTo !== null && v.effectiveDateTo < dayBefore)
    .sort((a, b) => (b.effectiveDateTo as string).localeCompare(a.effectiveDateTo as string))[0];
  const dayAfter = calendar.effectiveDateTo === null ? null : addDays(calendar.effectiveDateTo, 1);
  const next = (dayAfter && others.find(v => v.effectiveDateFrom === dayAfter)) || null;
  const later = others
    .filter(v => v.effectiveDateFrom > calendar.effectiveDateFrom)
    .sort((a, b) => a.effectiveDateFrom.localeCompare(b.effectiveDateFrom))[0];

  const startFloor = previous
    ? addDays(previous.effectiveDateFrom, 1)
    : before
      ? addDays(before.effectiveDateTo as string, 1)
      : today;
  const endMax = next
    ? next.effectiveDateTo === null
      ? addDays(OPEN_END_LATEST_START, -1)
      : addDays(next.effectiveDateTo, -1)
    : later
      ? addDays(later.effectiveDateFrom, -1)
      : STANDARD_MAX_DATE;

  const reason = later
    ? `Needs an end date: ${standardName(later)} starts on ${date(later.effectiveDateFrom)}`
    : from > OPEN_END_LATEST_START
      ? `Needs an end date: hours with no end must start by ${date(OPEN_END_LATEST_START)}`
      : null;

  return {
    startEditable: groupOf(calendar, today) === 'upcoming',
    start: { min: startFloor > today ? startFloor : today, max: OPEN_END_LATEST_START },
    end: { min: today, max: endMax },
    noEnd: { offered: reason === null, reason },
    previous,
    next,
  };
};

/**
 * What else the change moves (R3.4, R2.2): each adjoining set whose boundary moved, with its new
 * dates, and the note's sentences — including *No standard hours after {end}.* when the last set
 * gets an end.
 */
export const standardEditNote = (
  versions: readonly AuthoredOpeningHoursVersion[],
  calendar: AuthoredOpeningHoursVersion,
  fields: StandardFields,
  today: string,
): { moves: StandardMove[]; sentences: string[] } => {
  const { previous, next } = standardEditLimits(versions, calendar, today, fields.from);
  const moves: StandardMove[] = [];
  const sentences: string[] = [];
  if (previous && fields.from !== calendar.effectiveDateFrom) {
    const to = addDays(fields.from, -1);
    moves.push({ version: previous, from: previous.effectiveDateFrom, to });
    sentences.push(
      `${standardName(previous)} will end on ${date(to)} instead of ${date(
        previous.effectiveDateTo as string,
      )}.`,
    );
  }
  if (next && fields.to !== null && fields.to !== calendar.effectiveDateTo) {
    const from = addDays(fields.to, 1);
    moves.push({ version: next, from, to: next.effectiveDateTo });
    sentences.push(
      `${standardName(next)} will start on ${date(from)} instead of ${date(next.effectiveDateFrom)}.`,
    );
  }
  const last = !standardsOf(versions).some(v => v.effectiveDateFrom > calendar.effectiveDateFrom);
  if (last && fields.to !== null && fields.to !== calendar.effectiveDateTo) {
    sentences.push(`No standard hours after ${date(fields.to)}.`);
  }
  return { moves, sentences };
};

/**
 * Why Save holds (R3.5), else null: nothing differs from what is stored, the name is too long, an
 * end is missing while *No end date* is off, or a date is outside `standardEditLimits`.
 */
export const standardEditProblem = (
  versions: readonly AuthoredOpeningHoursVersion[],
  calendar: AuthoredOpeningHoursVersion,
  fields: { name: string; from: string | null; to: string | null; noEnd: boolean },
  today: string,
): string | null => {
  const nameProblem = standardNameProblem(fields.name);
  if (nameProblem) return nameProblem;
  if (!fields.from) return 'Choose when these hours start.';
  if (!fields.noEnd && !fields.to) return 'Choose when these hours end, or turn on No end date.';
  const to = fields.noEnd ? null : fields.to;
  const limits = standardEditLimits(versions, calendar, today, fields.from);
  if (to === null && !limits.noEnd.offered) return limits.noEnd.reason;
  if (limits.startEditable && fields.from !== calendar.effectiveDateFrom) {
    const max = to ?? limits.start.max;
    if (fields.from < limits.start.min || fields.from > max) {
      return `Choose a start from ${date(limits.start.min)} to ${date(max)}.`;
    }
  }
  if (to !== null && to !== calendar.effectiveDateTo) {
    const min = fields.from > limits.end.min ? fields.from : limits.end.min;
    if (to < min || to > limits.end.max) {
      return `Choose an end from ${date(min)} to ${date(limits.end.max)}.`;
    }
  }
  const same =
    standardNameStored(fields.name) === standardNameStored(calendar.name) &&
    fields.from === calendar.effectiveDateFrom &&
    to === calendar.effectiveDateTo;
  return same ? 'Nothing has changed yet.' : null;
};

/**
 * The step's announcement once a change has saved (R3.6), leaving out what did not change: the
 * rename, the new run, then each set that moved with it.
 */
export const standardChangedSentence = (
  before: AuthoredOpeningHoursVersion,
  fields: StandardFields,
  moves: readonly StandardMove[],
): string => {
  const name = standardName({ name: standardNameStored(fields.name) });
  const said: string[] = [];
  if (standardNameStored(fields.name) !== standardNameStored(before.name)) {
    said.push(`${standardName(before)} renamed ${name}.`);
  }
  if (fields.from !== before.effectiveDateFrom || fields.to !== before.effectiveDateTo) {
    said.push(
      `${name} now runs from ${date(fields.from)} ${
        fields.to === null ? 'with no end' : `until ${date(fields.to)}`
      }.`,
    );
  }
  for (const m of moves) {
    said.push(
      m.from !== m.version.effectiveDateFrom
        ? `${standardName(m.version)} now starts on ${date(m.from)}.`
        : `${standardName(m.version)} now ends on ${date(m.to as string)}.`,
    );
  }
  return said.join(' ');
};

/**
 * What *New Standard Hours From a Date* will do, said under its fields (R4.3): the set it ends and
 * copies, by name, and how the new one runs; with an end, that nothing follows it.
 */
export const newStandardSentence = (
  versions: readonly AuthoredOpeningHoursVersion[],
  { from, to }: { from: string; to: string | null },
): string => {
  const { copiedFrom, ends } = newStandardFromDate(versions, { from, to });
  const runs = to === null ? 'with no end' : `until ${date(to)}`;
  const after = to === null ? '' : ` No standard hours after ${date(to)}.`;
  if (ends) {
    return `${standardName(ends)} ends on ${date(addDays(from, -1))}. The new week starts as a copy of it, ${runs}.${after}`;
  }
  if (copiedFrom) {
    return `The new week follows on from ${standardName(copiedFrom)}, as a copy of it, ${runs}.${after}`;
  }
  return `The new week starts with every day Closed.${after}`;
};

// ── Special periods (spec twin-details-page-hours-special, R1) ──────────────────────────────

/** The store's longest special period name (`SPECIAL_NAME_MAX`), trimmed. */
export const SPECIAL_NAME_MAX = 80;
/** The store's latest date. */
export const SPECIAL_MAX_DATE = '2099-12-31';

export const SPECIAL_NAME_MISSING = 'A special period needs a name';
export const SPECIAL_NAME_TOO_LONG = `A special period's name can be at most ${SPECIAL_NAME_MAX} characters`;

/** Why a typed name cannot be saved (the store's own sentences), else null. */
export const specialNameProblem = (raw: string): string | null => {
  const name = raw.trim();
  if (name.length === 0) return SPECIAL_NAME_MISSING;
  if (name.length > SPECIAL_NAME_MAX) return SPECIAL_NAME_TOO_LONG;
  return null;
};

/** A stored special's name; one the store should never hold without reads as *Special period*. */
export const specialName = (v: Pick<AuthoredOpeningHoursVersion, 'name'>): string =>
  v.name ?? 'Special period';

/** A stored special's last day; one the store should never hold without is a one-day period. */
export const specialEnd = (
  v: Pick<AuthoredOpeningHoursVersion, 'effectiveDateFrom' | 'effectiveDateTo'>,
): string => v.effectiveDateTo ?? v.effectiveDateFrom;

const specialsOf = (versions: readonly AuthoredOpeningHoursVersion[]) =>
  versions.filter(v => v.kind === 'special');

/** The special periods by the twin's date, by `groupOf`: Now by start, Upcoming earliest first, Past latest end first. */
export const groupSpecials = (
  versions: readonly AuthoredOpeningHoursVersion[],
  today: string,
): {
  now: AuthoredOpeningHoursVersion[];
  upcoming: AuthoredOpeningHoursVersion[];
  past: AuthoredOpeningHoursVersion[];
} => {
  const of = (group: CalendarGroup) =>
    specialsOf(versions).filter(v => groupOf(v, today) === group);
  const byStart = (a: AuthoredOpeningHoursVersion, b: AuthoredOpeningHoursVersion) =>
    a.effectiveDateFrom.localeCompare(b.effectiveDateFrom);
  return {
    now: of('now').sort(byStart),
    upcoming: of('upcoming').sort(byStart),
    past: of('past').sort((a, b) => specialEnd(b).localeCompare(specialEnd(a))),
  };
};

/** The calendars list's groups: standards and special periods together (R4.1). */
export interface CalendarListGroups {
  /** The standard covering today, then any special covering today. */
  now: AuthoredOpeningHoursVersion[];
  /** By first day, a standard first on a shared date. */
  upcoming: AuthoredOpeningHoursVersion[];
  /** Both, latest end first. */
  past: AuthoredOpeningHoursVersion[];
}

export const listGroups = (
  versions: readonly AuthoredOpeningHoursVersion[],
  today: string,
): CalendarListGroups => {
  const standards = groupCalendars(versions, today);
  const specials = groupSpecials(versions, today);
  return {
    now: [...(standards.now ? [standards.now] : []), ...specials.now],
    // A stable sort over standards-then-specials keeps a standard first on a shared date.
    upcoming: [...standards.upcoming, ...specials.upcoming].sort((a, b) =>
      a.effectiveDateFrom.localeCompare(b.effectiveDateFrom),
    ),
    past: [...standards.past, ...specials.past].sort((a, b) =>
      specialEnd(b).localeCompare(specialEnd(a)),
    ),
  };
};

/** The standard a special is compared with: the one covering its first day, else null. */
export const comparisonStandard = (
  versions: readonly AuthoredOpeningHoursVersion[],
  special: Pick<AuthoredOpeningHoursVersion, 'effectiveDateFrom'>,
): AuthoredOpeningHoursVersion | null =>
  standardsOf(versions).find(v => covers(v, special.effectiveDateFrom)) ?? null;

/**
 * What a special's day was in the standard it is compared with, when the two differ (R1.4):
 * *Was 06:00 – 22:00*, *Was 06:00 – 12:00, 14:00 – 18:00*, *Was 06:00 – 01:00* (a past-midnight
 * close as stored), *Was 24 hours* or *Was Closed*. Null for an unchanged day, or no standard.
 */
export const wasLine = (
  standardDay: AuthoredOpeningHoursDay | null | undefined,
  specialDay: AuthoredOpeningHoursDay,
): string | null => {
  if (!standardDay || dayKey(standardDay) === dayKey(specialDay)) return null;
  if (standardDay.state === 'open_24h') return 'Was 24 hours';
  if (standardDay.state === 'closed' || standardDay.ranges.length === 0) return 'Was Closed';
  const spans = [...standardDay.ranges]
    .sort((a, b) => toMinutes(a.open) - toMinutes(b.open))
    .map(r => `${r.open.slice(0, 5)} – ${r.close.slice(0, 5)}`);
  return `Was ${spans.join(', ')}`;
};

/** How many of a special's days differ from the standard's same weekday. */
const differingDays = (
  special: Pick<AuthoredOpeningHoursVersion, 'days'>,
  standard: Pick<AuthoredOpeningHoursVersion, 'days'>,
): number =>
  WEEKDAYS.filter(w => dayKey(dayOf(special.days, w)) !== dayKey(dayOf(standard.days, w))).length;

/** Whether any of a special's days differs from the standard it is compared with. */
export const anyDayDiffers = (
  special: Pick<AuthoredOpeningHoursVersion, 'days'>,
  standard: Pick<AuthoredOpeningHoursVersion, 'days'>,
): boolean => differingDays(special, standard) > 0;

/** The footer's count (R1.5). */
export const differSentence = (
  special: Pick<AuthoredOpeningHoursVersion, 'days' | 'effectiveDateFrom'>,
  standard: Pick<AuthoredOpeningHoursVersion, 'days'> | null,
): string => {
  if (!standard) {
    return `No standard hours on ${date(special.effectiveDateFrom)} to compare with`;
  }
  const n = differingDays(special, standard);
  if (n === 0) return 'Same as standard hours on every day';
  return `${n} day${n === 1 ? ' differs' : 's differ'} from standard hours`;
};

/** The first standard starting after a special's first day and on or before its last (R1.6). */
export const standardChangeWithin = (
  versions: readonly AuthoredOpeningHoursVersion[],
  special: AuthoredOpeningHoursVersion,
): AuthoredOpeningHoursVersion | null =>
  [...standardsOf(versions)]
    .filter(
      v =>
        v.effectiveDateFrom > special.effectiveDateFrom &&
        v.effectiveDateFrom <= specialEnd(special),
    )
    .sort((a, b) => a.effectiveDateFrom.localeCompare(b.effectiveDateFrom))[0] ?? null;

/** R1.6's footer line, when a standard changes inside the period and there is one to compare with. */
export const standardChangeSentence = (
  versions: readonly AuthoredOpeningHoursVersion[],
  special: AuthoredOpeningHoursVersion,
): string | null => {
  const change = standardChangeWithin(versions, special);
  const compared = comparisonStandard(versions, special);
  if (!change || !compared) return null;
  return `Standard hours change on ${date(change.effectiveDateFrom)}. Days are compared with the hours from ${date(
    compared.effectiveDateFrom,
  )}.`;
};

/** *22 Dec 2026 – 2 Jan 2027*, or *22 Dec 2026* for one day — the store's span (`describeVersion`). */
export const specialSpan = (
  v: Pick<AuthoredOpeningHoursVersion, 'effectiveDateFrom' | 'effectiveDateTo'>,
): string => {
  const end = specialEnd(v);
  return end !== v.effectiveDateFrom
    ? `${date(v.effectiveDateFrom)} – ${date(end)}`
    : date(v.effectiveDateFrom);
};

/** The week panel's sub line for a special (frame 07, no zone). */
export const specialPanelSub = (
  v: Pick<AuthoredOpeningHoursVersion, 'effectiveDateFrom' | 'effectiveDateTo'>,
): string => `${specialSpan(v)} · replaces standard hours`;

/**
 * The first other special period sharing a date with `candidate` (R1.7), as the store's
 * `datesOverlap` decides, named as the store names it: *Overlaps Christmas closure (24 Dec 2026 – 26
 * Dec 2026)*. `ownKey` is the period being re-dated, which never clashes with itself. A special over
 * a standard is never a clash. The store stays the authority: its 409 is shown when the two disagree.
 */
export const specialClash = (
  versions: readonly AuthoredOpeningHoursVersion[],
  candidate: { from: string; to: string },
  ownKey: string | null,
): string | null => {
  const other = specialsOf(versions).find(
    v =>
      calendarKey(v) !== ownKey &&
      v.effectiveDateFrom <= candidate.to &&
      candidate.from <= specialEnd(v),
  );
  return other ? `Overlaps ${specialName(other)} (${specialSpan(other)})` : null;
};

/**
 * The body that adds a special period (R1.9), less the etag: the trimmed name, both dates, and the
 * days of the standard covering its first day, deep-copied; with none, every day Closed.
 */
export const newSpecialBody = (
  versions: readonly AuthoredOpeningHoursVersion[],
  { name, from, to }: { name: string; from: string; to: string },
): Omit<AuthoredSpecialWrite, 'expectedEtag'> => {
  const standard = comparisonStandard(versions, { effectiveDateFrom: from });
  return {
    kind: 'special',
    name: name.trim(),
    effectiveDateFrom: from,
    effectiveDateTo: to,
    days: standard ? cloneWeek(standard.days) : closedWeek(),
  };
};

/**
 * The dates a special period's field allows (R1.10): from today for a new or upcoming period, from
 * its own first day for a running one (so the field can show it); to the store's latest date.
 */
export const specialDateLimits = (
  special: AuthoredOpeningHoursVersion | null,
  group: CalendarGroup | null,
  today: string,
): { min: string; max: string } => ({
  min: special && group !== 'upcoming' ? special.effectiveDateFrom : today,
  max: SPECIAL_MAX_DATE,
});

/**
 * A running period's dates (ruling 1, R1.11): its first day stays, and its last day may move to end
 * it sooner, but never before today, so hours already past are never rewritten. Null when allowed.
 */
export const runningDatesProblem = (
  special: AuthoredOpeningHoursVersion,
  next: { from: string; to: string },
  today: string,
): string | null => {
  if (groupOf(special, today) !== 'now') return null;
  const name = specialName(special);
  if (next.from !== special.effectiveDateFrom) {
    return `${name} has started, so its first day stays ${date(
      special.effectiveDateFrom,
    )}. Move its last day to end it sooner.`;
  }
  if (next.to < today) {
    return `${name} can’t end before today, ${date(today)}. Its past days are kept as they were.`;
  }
  return null;
};
